import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons/faCircleCheck';
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons/faTriangleExclamation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    confirmEmailUnsubscribeThunk,
    fetchEmailUnsubscribeInfoThunk
} from 'actions/emailUnsubscribe/EmailUnsubscribeActions';
import Button from 'components/UI/V3/Button/Button';
import Spinner from 'components/UI/V3/Spinner/Spinner';
import useHideHubSpotChat from 'hooks/useHideHubSpotChat';
import { FunctionComponent, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { EmailUnsubscribeState } from 'reducers/emailUnsubscribe/EmailUnsubscribeState';
import { RootState } from 'store';

import styles from './EmailUnsubscribe.module.scss';

interface EmailUnsubscribeProps {
    token: string;
}

/**
 * Public unsubscribe page for campaign email recipients. Data + API calls
 * live in the `emailUnsubscribe` Redux slice (see
 * `actions/emailUnsubscribe/*`); this component only reads the slice flags
 * and dispatches the two thunks.
 *
 * Terminal by design — no bounce-back button, no redirect. Recipients are
 * clients of a business (not Cuddles account holders), so the marketplace
 * app's home has nothing useful for them to navigate to. The HubSpot chat
 * is hidden for the same reason (same pattern as `LeaveReview`).
 *
 * Render branch is derived from four flags on the slice:
 *   loading                         → Spinner
 *   invalid                         → error card
 *   justConfirmed || alreadyOptedOut → terminal success card (idempotent AC3)
 *   default (email loaded)          → confirm card
 */
const EmailUnsubscribe: FunctionComponent<EmailUnsubscribeProps> = ({ token }) => {
    const dispatch = useDispatch();
    const slice = useSelector<RootState, EmailUnsubscribeState>(state => state.emailUnsubscribe);

    useHideHubSpotChat(true);

    useEffect(() => {
        dispatch(fetchEmailUnsubscribeInfoThunk(token));
    }, [token, dispatch]);

    const handleConfirm = () => {
        dispatch(confirmEmailUnsubscribeThunk(token));
    };

    return (
        <div className={styles.container}>
            <div className={styles.card}>
                <img
                    className={styles.logo}
                    src="/images/logo/cuddles-logo.svg"
                    alt="Cuddles"
                />

                {renderStateContent(slice, handleConfirm)}
            </div>
        </div>
    );
};

// ---------------------------------------------------------------------------
// View branch selection
//
// The slice keeps flat flags rather than a discriminated union so the reducer
// stays trivial; the render-time union derivation lives here.
// ---------------------------------------------------------------------------

const renderStateContent = (slice: EmailUnsubscribeState, onConfirm: () => void) => {
    if (slice.loading) {
        return (
            <div className={styles.loaderRow}>
                <Spinner />
            </div>
        );
    }

    if (slice.invalid) {
        return (
            <>
                <div className={styles.iconInvalid}>
                    <FontAwesomeIcon icon={faTriangleExclamation} />
                </div>
                <h1 className={styles.title}>Invalid unsubscribe link</h1>
                <p className={styles.body}>
                    This link is invalid or has expired. If you&apos;re trying to unsubscribe, check your latest email
                    for a fresh link.
                </p>
            </>
        );
    }

    if (slice.justConfirmed || slice.alreadyOptedOut) {
        return (
            <>
                <div className={styles.iconSuccess}>
                    <FontAwesomeIcon icon={faCircleCheck} />
                </div>
                <h1 className={styles.title}>You&apos;ve been unsubscribed</h1>
                <p className={styles.body}>
                    You won&apos;t receive marketing emails from{' '}
                    <strong>{slice.marketplaceName ?? 'this business'}</strong> anymore.
                </p>
            </>
        );
    }

    // Default — confirm card. `email` is filled by a successful fetch; we
    // fall back to a neutral pronoun if the copy somehow renders before the
    // GET resolves (shouldn't happen thanks to the `loading` branch above).
    const email = slice.email ?? 'this address';
    const businessName = slice.marketplaceName ?? 'this business';

    return (
        <>
            <h1 className={styles.title}>Unsubscribe from emails</h1>
            <p className={styles.body}>
                You&apos;re about to unsubscribe <strong>{email}</strong> from <strong>{businessName}</strong>&apos;s
                marketing emails.
            </p>
            <p className={styles.body}>
                You won&apos;t receive any more marketing emails from <strong>{businessName}</strong>.
            </p>
            <div className={styles.actions}>
                <Button
                    label={slice.confirming ? 'Confirming…' : 'Confirm Unsubscribe'}
                    size="large"
                    variant="green"
                    onClick={onConfirm}
                    disabled={slice.confirming}
                    isLoading={slice.confirming}
                    className={styles.confirmButton}
                />
            </div>
        </>
    );
};

export default EmailUnsubscribe;
